import { Heading, Only, Section } from "src/utils"
import React, { useEffect, useRef, useState } from "react"
import cn from "classnames"
import { SanityImage, SanityMedia, ThemeColor } from "src/sanity/types"
import s from "./TestimonialsModule.module.scss"
import Icon, { IconType } from "src/components/icon/Icon"
import StarRating from "./StarRating"
import { Container } from "src/layout/container/Container"
import Image from "src/components/image/Image"
import { generateSanityImage, getAspectRatioOMedia } from "src/sanity/image"
import FeatureWalkthroughProgressBar from "../feature-walkthrough-module/FeatureWalkthroughProgressBar"
import { RollingText } from "src/components/rolling-text/RollingText"
import Media from "src/components/media/Media"
import TrustPilot from "src/components/trustpilot/Trustpilot"

export interface Person {
    byline1: string
    byline2?: string
    id: string
    body: string
    image: SanityImage
}

interface Provider {
    provider: "Facebook" | "App Store" | "Google Play" | "Trustpilot" | "custom_image"
    id: string
    rating?: number
    image?: SanityMedia
}

interface TestimonialsModuleProps {
    title: string
    media?: SanityMedia
    list: Person[]
    rows?: number
    providers: Provider[]
    scrollLogosHorizontally?: boolean
    backgroundColor: ThemeColor
}

function getProviderIcon(name: string) {
    switch (name) {
        case "Facebook":
            return IconType.FACEBOOK_SQUARE
        case "App Store":
            return IconType.APP_STORE_SQUARE
        case "Google Play":
            return IconType.GOOGLE_PLAY_SQUARE
        case "Trustpilot":
            return IconType.TRUSTPILOT_SQUARE
        default:
            return IconType.FACEBOOK_SQUARE
    }
}

export default function TestimonialsModule({ title, media, list, providers, scrollLogosHorizontally, backgroundColor }: TestimonialsModuleProps): JSX.Element {
    list = list || []

    const [activeTab, setActiveTab] = useState(0)
    const [visible, setVisible] = useState(false)
    // const [hasInteracted, setHasInteracted] = useState<boolean>(false)
    const tab = list[activeTab]
    const inViewRef = useRef()
    const slideDuration = 8000

    useEffect(() => {
        if (!inViewRef.current) {
            return
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setVisible(true)
                observer.disconnect()
            }
        }, { threshold: .65 })

        observer.observe(inViewRef.current)

        return () => {
            observer.disconnect()
        }
    }, [])

    useEffect(() => {
        if (visible) {
            const timeout = setTimeout(() => {
                setActiveTab(prev => prev === list.length - 1 ? 0 : prev + 1)
            }, slideDuration)

            return () => {
                clearTimeout(timeout)
            }
        }
    }, [tab, visible, list])

    const moduleContrastColor = ["primary-blue", "primary-green", "black", "paas-blue"].includes(backgroundColor)

    return (
        <Section>
            <div className={cn({ "has-background-color": !!backgroundColor, [`bg-${backgroundColor}`]: backgroundColor, "text-white": moduleContrastColor })} >
                <Container className={cn(s["testimonials"])} noMargin={scrollLogosHorizontally}>
                    {media?.type && (
                        <div className={cn(s["testimonials__image-container"])}>
                            {media && (<Media
                                media={media}
                                sizes={[
                                    "(min-width: 768px) 50vw",
                                    "100vw",
                                ]}

                                // Use a fixed dimension for videos to avoid too much bandwidth
                                width={
                                    media.type === "vimeoFiles" ? 800 / (media.height / media.width) : media.width}
                                height={
                                    media.type === "vimeoFiles" ? 800 : media.height
                                }

                                step={500}
                                alt=""
                                videoWithoutBorders
                            />
                            )}
                        </div>
                    )}
                    <div className={cn(s["testimonials__content"], (list.length && !title) ? s["testimonials__content--only-quotes"] : null)}>


                        <Only if={list?.length}>
                            {title && <Heading className={cn(s["testimonials__content__title"], "heading-300")}>{title}<Icon type={IconType.ARROW_TOP_RIGHT} className={"text-primary-green"} /></Heading>}

                            {providers?.length > 0 && (
                                <ul className={cn(s["testimonials__content__provider-rating"])}>
                                    {providers?.map((item, i) => <ProviderComponent key={i} item={item} />)}
                                </ul>
                            )}

                            {list.length > 1 && (
                                <div className={cn(s["testimonials__progress-bar"], "hide-on-mobile")}>
                                    {list.map((i, index) => {
                                        return (
                                            <FeatureWalkthroughProgressBar
                                                key={i.id}
                                                activeTab={activeTab}
                                                index={index}
                                                visible={visible}
                                                duration={slideDuration}
                                                setActive={() => setActiveTab(index)}
                                            />
                                        )
                                    })}
                                </div>
                            )}
                            <div className={s["testimonials__container"]} ref={inViewRef}>
                                <Icon style={{ width: "32px", marginBottom: "1em" }} type={IconType.QUOTE} />
                                <RollingText className={s["testimonials__container__rolling-text"]} animation="slide-right" forcedActiveIndex={activeTab} rollingElements={list.map(person => (
                                    <div key={person.id} className={cn(s["testimonials__review"])} data-nosnippet>
                                        <p className="paragraph-400" >{person.body}</p>
                                        <div className={cn(s["testimonials__review__byline"])}>
                                            {person.image.type === "image" && person.image.src && (
                                                <span className={cn(s["testimonials__review__byline__image"])}>
                                                    <Image
                                                        {...generateSanityImage({
                                                            image: person.image,
                                                            width: 88,
                                                            height: 88,
                                                        })}
                                                        alt={person.byline1}
                                                        sizes={["5em"]}
                                                    />
                                                </span>
                                            )}
                                            {person.byline1}
                                        </div>
                                    </div>
                                ))}
                                />

                            </div>
                            {list.length > 1 && (
                                <div className={cn(s["testimonials__progress-bar"], "hide-on-desktop")}>
                                    {list.length > 1 && list.map((i, index) => {
                                        return (
                                            <FeatureWalkthroughProgressBar
                                                key={i.id}
                                                activeTab={activeTab}
                                                index={index}
                                                visible={visible}
                                                duration={slideDuration}
                                                setActive={() => setActiveTab(index)}
                                            />
                                        )
                                    })}
                                </div>
                            )}
                        </Only>
                        <Only if={!list.length && providers}>
                            {title && <Heading className={cn(s["testimonials__content__title"], "heading-300 text-center")}>{title}<Icon type={IconType.ARROW_TOP_RIGHT} className={"text-primary-green"} /></Heading>}
                            <div className={cn(s["testimonials__ratings-outer"], scrollLogosHorizontally ? s["testimonials__ratings-outer--scroll"] : null)}>
                                {Array(scrollLogosHorizontally ? 2 : 1).fill(null).map((_, index) => (
                                    <ul className={s["testimonials__ratings"]} key={index}>
                                        {providers?.map(item => <ProviderComponent key={item.id} item={item} />)}
                                    </ul>
                                ))}
                            </div>
                        </Only>


                    </div>

                    <script type='application/ld+json'
                        dangerouslySetInnerHTML={{ __html: JSON.stringify({ "@context": "http://schema.org", "@type": "SoftwareApplication", "name": "Lunar", "description": "Gain control of your money and grow them bigger. Avoid financial worries and be able to buy more of what you dream of. \n\nDo what 500.00 have already done and get a free account, card, and the best banking app in Denmark. The app provides you with everything you need to manage your money smarter than ever.\n\nGet Lunar for free now. Without leaving your current bank.\n\nLunar Private - Denmark\n\n· Free app, Visa Debit card and account\n· Goals: Save up for your dreams, easier than ever\n· Spend: The perfect budgeting tool for you to stay on top of your spending\n· Transfer: Bank transfers, real-time and scheduled\n· Freeze Card: Temporarily freeze your card from the app\n· Support: Live support directly from the app\n\nLunar is all you need moneywise\n\nWe are disrupting the banking experience. Our app is tailored to a digital experience, making it easier than ever to manage your money and stay on top of your economy.\n\nWhy not try us out? It’s free, no commitments, you don’t even have to change banks to be a Lunar user and it only requires a few minutes of your time to sign up.\n\nPlease note: Our product varies depending on your country of residence — read more below.\n\nLunar Business - Denmark\n\n· Business account with Easy Account support\n· Get +1% interest rate of up to 25,000 DKK\n· No negative interest up to 10 mil DKK\n· A prepaid Mastercard\n· Pay My Bill: Automatic, hassle free payment of your bills\n· Real-time bank transfers: No waiting for processing\n· Save receipt: Store your most important receipts with their corresponding transactions\n· Free in-app support chat\n\nLunar Private - Sweden\n\n· Lunar Black Card: Prepaid Mastercard\n- Get 1,05% interest rate of up to 100.000 SEK\n· Spend: The perfect budgeting tool for you to stay on top of your spending\n· International transfers: Low cost, high speed International transfers\n· Multi currency card: switch between SEK, NOK, GBP and EUR (no fee withdrawals and transfers)\n· Freeze card: Temporarily freeze your card from the app\n· Support: Live support directly from the app\n· Safety first: Best-in-class security with BankID\n\nLunar Private - Norway\n\n· Free app, Visa-card and account\n· Get 1% interest of up to 100 000 kr on your spending account\n· Goals: Save up for your dreams, easier than ever.\n· Spend: The perfect budgeting tool for you to stay on top of your spending\n· Transfer: Bank transfers, real-time and scheduled\n· Freeze Card: Temporarily freeze your card from the app\n· Support: Live support directly from the app or by phone\n· Safety first: Best-in-class security with BankID\n\nLunar is all you need moneywise\n\nWe are disrupting the banking experience. Our app is tailored to a digital experience, making it easier than ever to manage your money and stay on top of your finances.\nWhy not try us out? It’s free, no commitments, you don’t even have to change banks to be a Lunar user and it only requires a few minutes of your time to sign up.\n\nDO LIKE 650,000 OTHERS AND GET A LUNAR ACCOUNT FOR FREE NOW. YOU CAN EASILY KEEP YOUR OLD BANK.\n\nStay up-to-date with www.lunar.app and follow us on social media:\nFacebook: @LunarDanmark @LunarSverige @LunarNorge\nInstagram: @lunar\nSnapchat: @lunarway\nTwitter: @lunarbank\nLinkedIn: Lunar", "screenshot": ["https://is3-ssl.mzstatic.com/image/thumb/PurpleSource112/v4/bd/db/1c/bddb1ce7-bef5-830f-a9a9-67ad49e06dd9/2e0033cb-3ec8-4b13-9b85-31d37040cdc9_170.png/300x0w.jpg", "https://is2-ssl.mzstatic.com/image/thumb/PurpleSource112/v4/aa/0f/6a/aa0f6ab1-f148-2ffc-8d96-549ac63e5850/33010a58-af62-42ed-8ca6-3b64e7899e0f_170-1.png/300x0w.jpg", "https://is3-ssl.mzstatic.com/image/thumb/PurpleSource112/v4/d9/58/b2/d958b25c-cb40-20f2-add2-0c41b02a60f2/0d196cbe-8c1e-4b77-a85b-006ea3332c39_170-2.png/300x0w.jpg", "https://is5-ssl.mzstatic.com/image/thumb/PurpleSource112/v4/d8/00/78/d80078c6-c298-7c27-9ee0-ac584faf4ea3/d0935c24-c259-415b-bc44-758bd7bed183_171.png/300x0w.jpg", "https://is1-ssl.mzstatic.com/image/thumb/PurpleSource112/v4/3d/27/8a/3d278ac4-cef2-6862-a8a6-8494ca8e0fb9/cf06b75e-5af7-4da3-ab75-2882038c470e_172.png/300x0w.jpg", "https://is2-ssl.mzstatic.com/image/thumb/PurpleSource122/v4/90/0f/05/900f057b-4502-c10b-60c9-cbcbf30140c9/9c3e6db1-7308-4866-8093-25a1998dfcf3_177.png/300x0w.jpg", "https://is3-ssl.mzstatic.com/image/thumb/PurpleSource122/v4/6c/d6/36/6cd636be-c5d4-b463-3324-13f98ec4a58d/a8fc265b-5453-4f24-b62c-3158b670b3e5_176.png/300x0w.jpg", "https://is2-ssl.mzstatic.com/image/thumb/PurpleSource122/v4/61/d0/da/61d0dacf-6cea-9796-fb80-e7c97e28ed4e/73cf7109-be1c-472f-bf65-3c540d10ef0c_175.png/300x0w.jpg", "https://is4-ssl.mzstatic.com/image/thumb/PurpleSource112/v4/01/77/c5/0177c5b4-5c52-61a0-6495-cc2a855b9b96/dd2cc2f3-4e43-4b3c-bfb7-9a388dbf1170_174.png/300x0w.jpg", "https://is2-ssl.mzstatic.com/image/thumb/PurpleSource122/v4/cf/b4/87/cfb48717-b5cb-c46f-c717-08754da0eeed/febf1ad2-d466-4bce-b4a0-a5828d3d1e27_173.png/300x0w.jpg", "https://is2-ssl.mzstatic.com/image/thumb/Purple112/v4/d1/2f/e8/d12fe8a2-7570-91fd-f493-7d9ba38c04a6/pr_source.png/643x0w.jpg", "https://is5-ssl.mzstatic.com/image/thumb/Purple112/v4/a8/2f/5b/a82f5bfa-0f1d-d8c4-5322-529055570c1e/0785324c-95b6-4d1d-93d3-5ed5df2c3998_Ipad_EN.png/643x0w.jpg", "https://is5-ssl.mzstatic.com/image/thumb/Purple112/v4/d3/b2/f2/d3b2f267-1047-1d3a-c11a-63b2bd09ddfb/pr_source.png/643x0w.jpg", "https://is5-ssl.mzstatic.com/image/thumb/Purple112/v4/76/a7/86/76a786fd-9e3d-2b3e-55c5-8836e8e364c8/pr_source.png/643x0w.jpg", "https://is5-ssl.mzstatic.com/image/thumb/Purple112/v4/35/52/be/3552be3d-e54d-c664-619a-a26c6f7e4d53/pr_source.png/643x0w.jpg", "https://is4-ssl.mzstatic.com/image/thumb/Purple112/v4/7c/89/bb/7c89bb80-f171-21a5-16a3-7c1f0cbca9e5/pr_source.png/643x0w.jpg"], "image": "https://is3-ssl.mzstatic.com/image/thumb/Purple113/v4/d2/c3/25/d2c32586-22d5-e5be-f016-58317411c020/AppIcon-1x_U007emarketing-0-7-0-sRGB-85-220.png/1200x630wa.png", "applicationCategory": "Finance", "datePublished": "18 Apr 2016", "operatingSystem": "iOS, Android, Web", "author": { "@type": "Person", "name": "Lunar Way", "url": "https://apps.apple.com/dk/developer/lunar-way/id1068850212" }, "aggregateRating": { "@type": "AggregateRating", "ratingValue": 4.7, "reviewCount": 22632 }, "offers": { "@type": "Offer", "price": 0, "priceCurrency": "DKK", "category": "free" } }) }} >
                    </script>
                </Container>
            </div>
        </Section>
    )
}

function ProviderComponent({ item }: { item: Provider }) {
    return (
        <li key={item.id} className={cn(s["testimonials__ratings__item"], item.provider === "custom_image" ? s["testimonials__image__item"] : undefined)}>
            {item.provider !== "custom_image" && (
                item.provider === "Trustpilot" ?
                    (<TrustPilot />) :
                    (
                        <Section>
                            <Icon className={s["testimonials__ratings__item__icon"]} type={getProviderIcon(item.provider)} />
                            <div>
                                <Heading className="paragraph-100">{item.provider}</Heading>
                                <StarRating rating={item.rating} className={s["testimonials__ratings__item__rating"]} />
                            </div>
                        </Section>
                    )
            )}

            {item.provider === "custom_image" && item?.image?.type === "image" && (
                <Image
                    sizes={[]}
                    {...generateSanityImage({
                        image: item.image,
                        width: 1200,
                        height: Math.ceil(1200 / ((item.image.height && item.image.width) ? getAspectRatioOMedia(item.image) : 4 / 3)),
                        step: 500,
                    })}
                    alt={item.image.alt}
                />
            )}

        </li>
    )
}
